import { Component, Input, Output, EventEmitter } from "@angular/core";
import { CommonModule } from "@angular/common";
import { MatIconModule } from "@angular/material/icon";

type DisplaySlot =
  | BundleProductDto
  | {
      isPlaceholder: true;
      placeholderText: string;
      showFreeShipping?: boolean;
      discountPercentage?: number;
    };

import { BundleProductListItemSelectedComponent } from "../bundle-product-list-item-selected/bundle-product-list-item-selected.component";
import {
  BundleProductDto,
  IBundleItemRewardSetting,
} from "../bundle.interfaces";

@Component({
  selector: "lib-bundle-product-list-item-selected-list",
  standalone: true,
  imports: [
    CommonModule,
    BundleProductListItemSelectedComponent,
    MatIconModule,
  ],
  templateUrl: "./bundle-product-list-item-selected-list.component.html",
  styleUrl: "./bundle-product-list-item-selected-list.component.scss",
})
export class BundleProductListItemSelectedListComponent {
  @Input()
  set bundleSize(size: number) {
    this._selectedProducts = [];
    this._bundleSize = size;
    this.updateDisplaySlots();
  }
  private _bundleSize: number = 4;

  private _selectedProducts: BundleProductDto[] = [];
  @Input()
  set selectedProducts(products: BundleProductDto[]) {
    if (products.length > this._bundleSize) {
      this._selectedProducts = products.slice(0, this._bundleSize);
    } else {
      this._selectedProducts = products;
    }
    this.updateDisplaySlots();
  }
  get selectedProducts(): BundleProductDto[] {
    return this._selectedProducts;
  }
  displaySlots: DisplaySlot[] = [];
  @Input() rewardsSettings: IBundleItemRewardSetting[] = [];
  @Output() remove = new EventEmitter<BundleProductDto>();

  private updateDisplaySlots(): void {
    const slots: DisplaySlot[] = [];
    let currentSlotIndex = 0;

    // Add products based on their quantity, up to bundle size
    for (const product of this._selectedProducts) {
      const quantity = product.quantity || 1; // Default to 1 if quantity is missing
      for (let i = 0; i < quantity; i++) {
        if (currentSlotIndex < this._bundleSize) {
          slots.push(product);
          currentSlotIndex++;
        } else {
          break; // Stop adding this product if bundle size is reached
        }
      }
      if (currentSlotIndex >= this._bundleSize) {
        break; // Stop adding more products if bundle size is reached
      }
    }
    // Fill remaining slots with placeholders
    const filledSlots = slots.length;
    for (let i = filledSlots; i < this._bundleSize; i++) {
      const itemPosition = i + 1; // 1-based position
      const placeholderText = `Item ${itemPosition}`;

      // Find the highest applicable reward for this item position
      // Look for rewards where itemCountToActivateReward <= itemPosition
      const applicableRewards = this.rewardsSettings.filter(
        (r) => r.itemCountToActivateReward <= itemPosition,
      );

      // Get the reward with the highest itemCountToActivateReward (most specific)
      const applicableReward = applicableRewards.reduce(
        (highest, current) => {
          if (
            !highest ||
            current.itemCountToActivateReward >
              highest.itemCountToActivateReward
          ) {
            return current;
          }
          return highest;
        },
        null as IBundleItemRewardSetting | null,
      );

      const showFreeShipping =
        applicableReward?.rewardFixedShippingCost != null &&
        applicableReward?.rewardFixedShippingCost > 0;

      // Only show discount if it exists and is greater than 0
      const discountPercentage = applicableReward?.rewardDiscountPercentage;
      const shouldShowDiscount =
        discountPercentage != null && discountPercentage > 0;

      slots.push({
        isPlaceholder: true,
        placeholderText: placeholderText,
        showFreeShipping: showFreeShipping,
        discountPercentage: shouldShowDiscount ? discountPercentage : undefined,
      });
    }

    this.displaySlots = slots;
  }

  isPlaceholder(
    slot: DisplaySlot,
  ): slot is { isPlaceholder: true; placeholderText: string } {
    return (
      typeof slot === "object" &&
      slot !== null &&
      "isPlaceholder" in slot &&
      slot.isPlaceholder === true
    );
  }

  isProduct(slot: DisplaySlot): slot is BundleProductDto {
    return (
      typeof slot === "object" && slot !== null && !("isPlaceholder" in slot)
    );
  }

  onRemove(product: BundleProductDto) {
    this.remove.emit(product);
  }

  getSuffix(i: number) {
    const j = i % 10,
      k = i % 100;
    if (j == 1 && k != 11) {
      return "st";
    }
    if (j == 2 && k != 12) {
      return "nd";
    }
    if (j == 3 && k != 13) {
      return "rd";
    }
    return "th";
  }
}
